import { DEFAULT_CUSTOMIZATION } from "../../../store/tenancyCustomization/constants";
import HeaderMenuItem, { IHeaderMenuItemProps } from "./HeaderMenuItem";
import "./style.css";

interface IHeaderNavigationBarProps {
    companyName?: string;
    logo?: string;
    logoDimensions?: {
        width: number | string;
        height: number | string;
    };
    leftItems?: IHeaderMenuItemProps[];
    rightItems?: IHeaderMenuItemProps[];
    businessAreaSelectMenu?: React.ReactNode;
    userProfileMenu?: React.ReactNode;
    onLogout?: () => void;
    colors?: Partial<ICustomizationData>;
    testId?: string;
}

/**
 * Pure presentation component for the header navigation bar. Does not contain any business logic or state management.
 * The leftItems and rightItems props allow for flexible configuration of the menu items on either side of the header.
 * Each item should conform to the IHeaderMenuItemProps interface, which includes properties for title, icon, active state, and click handling.
 * The component also includes the company logo and name on the left, and a user profile menu on the right.
 * The BusinessAreaSelectMenu is included on the right side by default.
 */
export default function HeaderNavigationBar({
    companyName = "Pretectum",
    logo,
    logoDimensions,
    leftItems = [],
    rightItems = [],
    businessAreaSelectMenu,
    userProfileMenu,
    onLogout,
    colors,
    testId,
}: IHeaderNavigationBarProps) {
    const logoUrl = logo || "/icons/header-menu/pretectum-logo.png";
    const logoSize = logoDimensions || { height: "32px", width: "120px" };

    return (
        <div
            className="header-menu flex w-full items-center justify-between"
            style={{
                backgroundColor: colors?.colorNavigationBar || DEFAULT_CUSTOMIZATION.colorNavigationBar,
                color: colors?.colorNavigationFont || DEFAULT_CUSTOMIZATION.colorNavigationFont,
            }}
            data-testid={testId}
        >
            {/* Left side - Logo and main navigation */}
            <div className="flex items-center">
                {/* Logo */}
                <div className="flex items-center px-4">
                    <img
                        src={logoUrl}
                        alt="Company Logo"
                        style={{
                            maxHeight: logoSize.height,
                            maxWidth: logoSize.width,
                            objectFit: "contain",
                            height: "auto",
                            marginRight: "8px",
                        }}
                    />
                    <span className="font-medium" style={{ color: colors?.colorNavigationFont || DEFAULT_CUSTOMIZATION.colorNavigationFont }}>
                        {companyName}
                    </span>
                </div>

                {/* Main Navigation Items */}
                {leftItems.map((item, index) => (
                    <HeaderMenuItem key={index} {...item} colors={colors} />
                ))}
            </div>

            {/* Right side - User actions */}
            <div className="flex items-center">
                {businessAreaSelectMenu}

                {rightItems.map((item, index) => (
                    <HeaderMenuItem key={index} {...item} colors={colors} />
                ))}

                {userProfileMenu && <div className="header-menu-item">{userProfileMenu}</div>}
            </div>
        </div>
    );
}
